import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';
import { useAuth } from '../../context/AuthContext';

const nav = [
  { to: '/dashboard', icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats', icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads', icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm', icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents', icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets', icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot', icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api', icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding', icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget', icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics', icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing', icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings', icon: '⚙️', label: 'Settings' }
];

export default function Widget() {
  const { user } = useAuth();
  const [script, setScript] = useState('');
  const [botId, setBotId] = useState('');
  const [copied, setCopied] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    welcome_message: 'Hi! How can we help you today?',
    offline_message: 'We are offline. Please leave a message.',
    widget_position: 'bottom_right',
    greeting_enabled: 1,
    greeting_delay_seconds: 5,
    auto_greeting_message: 'Hello! Need help? We are here for you.'
  });
  const [aiForm, setAiForm] = useState({ ai_enabled: false, ai_instructions: '' });
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const hasAI = user?.has_ai === 1 || user?.has_ai === true || user?.plan_key === 'pack_advanced';

  useEffect(() => {
    axios.get('/api/business/script').then(r => {
      setScript(r.data.data.script);
      setBotId(r.data.data.bot_id);
    }).catch(() => {});

    axios.get('/api/business/profile').then(r => {
      const d = r.data.data;
      setProfile(d);
      setForm(f => ({
        ...f,
        welcome_message: d.welcome_message || f.welcome_message,
        offline_message: d.offline_message || f.offline_message,
        widget_position: d.widget_position || f.widget_position,
        greeting_enabled: d.greeting_enabled ?? 1,
        greeting_delay_seconds: d.greeting_delay_seconds || 5,
        auto_greeting_message: d.auto_greeting_message || f.auto_greeting_message
      }));
      setAiForm({
        ai_enabled: d.ai_enabled === 1 || d.ai_enabled === true,
        ai_instructions: d.ai_instructions || ''
      });
    }).catch(() => {});
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(script);
    setCopied(true);
    toast.success('Copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const save = async () => {
    setLoading(true);
    try {
      await axios.put('/api/business/widget', form);
      toast.success('Widget settings saved!');
    } catch { toast.error('Save failed'); }
    finally { setLoading(false); }
  };

  const saveAI = async () => {
    setAiLoading(true);
    try {
      await axios.put('/api/business/ai-settings', aiForm);
      toast.success('AI settings saved!');
    } catch { toast.error('Save failed'); }
    finally { setAiLoading(false); }
  };

  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ padding: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Widget & Script</h1>
        <p style={{ color: '#555', fontSize: 13, marginBottom: 28 }}>Get your embed code and configure the chat widget</p>

        {/* Embed Script */}
        <div style={{ background: '#0a1a0d', border: '1px solid rgba(0,213,91,0.3)', borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: '#00d55b' }}>🔌 Embed Script</h3>
            <button onClick={copy} style={{ padding: '7px 16px', background: copied ? 'transparent' : '#00d55b', color: copied ? '#00d55b' : '#000', border: copied ? '1px solid #00d55b' : 'none', borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              {copied ? '✓ Copied!' : 'Copy Script'}
            </button>
          </div>
          <pre style={{ background: '#000', border: '1px solid #1e1e1e', borderRadius: 8, padding: '14px 16px', fontFamily: 'monospace', fontSize: 12, color: '#00d55b', overflowX: 'auto', lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {script || 'Loading your script...'}
          </pre>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {['Paste before </body> tag on your website', 'Works on any website — WordPress, Wix, Shopify, HTML', 'Chat bubble appears instantly after adding the script'].map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 8 }}>
                <span style={{ color: '#00d55b' }}>→</span>
                <span style={{ color: '#888', fontSize: 12 }}>{t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bot ID */}
        <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 16, marginBottom: 24, display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Your Bot ID</div>
            <div style={{ fontFamily: 'monospace', fontSize: 14, color: '#00d55b' }}>{botId}</div>
          </div>
          <button onClick={() => { navigator.clipboard.writeText(botId); toast.success('Bot ID copied!'); }} style={{ padding: '8px 14px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>Copy</button>
        </div>

        {/* Widget Settings */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600 }}>Messages</h3>
            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Welcome message</label>
              <textarea value={form.welcome_message} onChange={e => set('welcome_message', e.target.value)} rows={2} style={{ resize: 'vertical' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Offline message</label>
              <textarea value={form.offline_message} onChange={e => set('offline_message', e.target.value)} rows={2} style={{ resize: 'vertical' }} />
            </div>
          </div>
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600 }}>Behaviour</h3>
            <div>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Widget position</label>
              <select value={form.widget_position} onChange={e => set('widget_position', e.target.value)}>
                <option value="bottom_right">Bottom Right</option>
                <option value="bottom_left">Bottom Left</option>
              </select>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13 }}>Auto greeting</div>
                <div style={{ fontSize: 11, color: '#555' }}>Widget auto-opens after delay</div>
              </div>
              <div onClick={() => set('greeting_enabled', form.greeting_enabled ? 0 : 1)}
                style={{ width: 44, height: 24, borderRadius: 12, background: form.greeting_enabled ? '#00d55b' : '#333', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                <div style={{ position: 'absolute', top: 3, left: form.greeting_enabled ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
              </div>
            </div>
            {Boolean(form.greeting_enabled) && <>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Delay (seconds)</label>
                <input type="number" value={form.greeting_delay_seconds} onChange={e => set('greeting_delay_seconds', e.target.value)} min={1} max={60} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Greeting message</label>
                <input value={form.auto_greeting_message} onChange={e => set('auto_greeting_message', e.target.value)} />
              </div>
            </>}
          </div>
        </div>

        <button onClick={save} disabled={loading} style={{ padding: '12px 32px', background: '#00d55b', color: '#000', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: loading ? 0.7 : 1, marginBottom: 32 }}>
          {loading ? 'Saving...' : '✓ Save Settings'}
        </button>

        {/* AI CHATBOT SECTION — Pack Advanced only */}
        {hasAI ? (
          <div style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.1), rgba(99,58,237,0.05))', border: '1px solid rgba(124,58,237,0.4)', borderRadius: 16, padding: 24, marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(124,58,237,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🤖</div>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#a78bfa' }}>AI Chatbot</h3>
                <p style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Powered by Claude AI — auto-replies when agents are offline</p>
              </div>
              {/* Enable/Disable Toggle */}
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: aiForm.ai_enabled ? '#a78bfa' : '#555' }}>
                  {aiForm.ai_enabled ? '🟣 Active' : '⚫ Inactive'}
                </span>
                <div onClick={() => setAiForm(f => ({ ...f, ai_enabled: !f.ai_enabled }))}
                  style={{ width: 44, height: 24, borderRadius: 12, background: aiForm.ai_enabled ? '#7c3aed' : '#333', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                  <div style={{ position: 'absolute', top: 3, left: aiForm.ai_enabled ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                </div>
              </div>
            </div>

            {/* How it works */}
            <div style={{ background: 'rgba(124,58,237,0.08)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
              {[
                ['💬', 'Visitor sends message', 'When no agent is online'],
                ['🤖', 'AI responds instantly', 'Using Claude AI 24/7'],
                ['👤', 'Agent takes over', 'When agent comes online']
              ].map(([icon, title, sub]) => (
                <div key={title} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#c4b5fd' }}>{title}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{sub}</div>
                </div>
              ))}
            </div>

            {/* AI Instructions */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: '#a78bfa', display: 'block', marginBottom: 6, fontWeight: 600 }}>
                AI Instructions <span style={{ color: '#555', fontWeight: 400 }}>(optional)</span>
              </label>
              <textarea
                value={aiForm.ai_instructions}
                onChange={e => setAiForm(f => ({ ...f, ai_instructions: e.target.value }))}
                placeholder={`Tell the AI about your business. Example:\n\nWe are a software company. We sell CRM and ERP solutions. Our pricing starts at ₹5,000/month. Support hours are 9am-6pm IST. For urgent issues, ask visitors to call +91 9999999999.`}
                rows={5}
                style={{ resize: 'vertical', borderColor: 'rgba(124,58,237,0.3)', background: 'rgba(124,58,237,0.05)', color: '#fff' }}
              />
              <p style={{ fontSize: 11, color: '#555', marginTop: 5 }}>
                The AI will use these instructions to answer visitor questions. The more details you add, the better the AI performs.
              </p>
            </div>

            <button onClick={saveAI} disabled={aiLoading}
              style={{ padding: '11px 28px', background: '#7c3aed', color: '#fff', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none', opacity: aiLoading ? 0.7 : 1 }}>
              {aiLoading ? 'Saving...' : '✓ Save AI Settings'}
            </button>
          </div>
        ) : (
          /* Upgrade prompt for non-advanced plans */
          <div style={{ background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 16, padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🤖</div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#a78bfa', marginBottom: 8 }}>AI Chatbot — Pack Advanced</h3>
            <p style={{ color: '#555', fontSize: 13, marginBottom: 16, lineHeight: 1.7 }}>
              Upgrade to Pack Advanced to enable AI auto-replies.<br />
              The AI responds to visitors 24/7 even when your agents are offline.
            </p>
            <a href="/dashboard/billing" style={{ display: 'inline-block', padding: '10px 28px', background: '#7c3aed', color: '#fff', borderRadius: 10, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
              Upgrade to Pack Advanced →
            </a>
          </div>
        )}
      </div>
    </DashLayout>
  );
}